/**
 * Browser print fallback transport.
 *
 * When no USB/network printer is configured, this opens the browser's
 * native print dialog with the PDF receipt (existing server-generated PDF flow).
 * This is the fallback — no ESC/POS encoding needed; it downloads the PDF
 * and triggers window.print() on an iframe.
 */
import type { PrinterTransport, PrinterDeviceInfo } from '../types';

export class BrowserPrintTransport implements PrinterTransport {
  readonly type = 'browser' as const;
  private iframe: HTMLIFrameElement | null = null;

  async connect(): Promise<void> {
    // Browser print is always available — no connection needed
  }

  async disconnect(): Promise<void> {
    this.cleanupIframe();
  }

  /**
   * For browser fallback, the `data` parameter is expected to be a PDF blob
   * as Uint8Array, NOT ESC/POS bytes. The caller should pass the raw PDF bytes.
   */
  async write(data: Uint8Array): Promise<void> {
    const blob = new Blob([data as unknown as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    try {
      await this.printViaIframe(url);
    } finally {
      // Defer cleanup to ensure print dialog has loaded
      setTimeout(() => {
        URL.revokeObjectURL(url);
        this.cleanupIframe();
      }, 5000);
    }
  }

  isConnected(): boolean {
    return true; // Always available
  }

  getDeviceInfo(): PrinterDeviceInfo {
    return {
      name: 'Browser Print Dialog',
      type: 'browser',
    };
  }

  private printViaIframe(url: string): Promise<void> {
    return new Promise((resolve) => {
      this.cleanupIframe();

      this.iframe = document.createElement('iframe');
      this.iframe.style.position = 'fixed';
      this.iframe.style.left = '-9999px';
      this.iframe.style.top = '-9999px';
      this.iframe.style.width = '0';
      this.iframe.style.height = '0';
      this.iframe.src = url;

      this.iframe.onload = () => {
        try {
          this.iframe?.contentWindow?.print();
        } catch {
          // Some browsers block cross-origin print; open in new tab instead
          window.open(url, '_blank');
        }
        resolve();
      };

      document.body.appendChild(this.iframe);
    });
  }

  private cleanupIframe(): void {
    if (this.iframe && this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
    }
    this.iframe = null;
  }
}
